import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Users, Plus, Trophy, Lightning, Target, WarningCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function ParentSpace() {
  const { user } = useAuth();
  const [children, setChildren] = useState([]);
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);

  const load = () => api.get("/parent/children").then((r) => setChildren(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const link = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/parent/link", { code });
      toast.success("Enfant lié avec succès !");
      setCode("");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  return (
    <div className="space-y-8">
      <div className="animate-fade-up">
        <h1 className="font-display text-3xl font-extrabold sm:text-4xl">Espace parent</h1>
        <p className="mt-1 text-ink/65">Suivez la progression de votre/vos enfant(s), {user?.name?.split(" ")[0]}.</p>
      </div>

      <form onSubmit={link} className="flex flex-col gap-3 rounded-2xl border border-border bg-white p-5 sm:flex-row sm:items-center">
        <div className="flex items-center gap-2 text-sm font-semibold text-forest"><Plus weight="bold" /> Lier un enfant :</div>
        <Input data-testid="child-code" value={code} onChange={(e) => setCode(e.target.value)} required
          placeholder="code de partage de l'élève (ex : YAYA01)" className="flex-1 rounded-xl" />
        <Button data-testid="link-child-btn" type="submit" disabled={loading} className="rounded-full bg-terracotta text-white hover:bg-[#E03E00]">
          {loading ? "Liaison…" : "Lier"}
        </Button>
      </form>
      <p className="-mt-4 text-xs text-muted-foreground">Votre enfant trouve son code de partage sur son tableau de bord (« Mon code de partage »).</p>

      {children.length ? (
        <div className="space-y-5">
          {children.map((c) => (
            <div key={c.user_id} data-testid={`child-${c.user_id}`} className="rounded-2xl border border-border bg-white p-6">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="grid h-11 w-11 place-items-center rounded-xl bg-forest/10 text-forest"><Users weight="fill" size={22} /></div>
                  <div>
                    <div className="font-display text-lg font-bold">{c.name}</div>
                    <div className="text-sm text-muted-foreground">{c.exam?.name || "Examen non choisi"} · Niveau {c.level.level} ({c.level.name})</div>
                  </div>
                </div>
                <span className="rounded-full bg-gold/15 px-3 py-1 text-sm font-bold text-[#9a6b00]">{c.xp} XP</span>
              </div>
              <div className="mt-5 grid grid-cols-3 gap-3">
                <div className="rounded-xl bg-cream p-4"><Target className="text-terracotta" size={20} /><div className="mt-1 font-display text-xl font-extrabold">{c.quizzes_done}</div><div className="text-xs text-muted-foreground">Quiz</div></div>
                <div className="rounded-xl bg-cream p-4"><Lightning className="text-forest" size={20} /><div className="mt-1 font-display text-xl font-extrabold">{c.avg_score}%</div><div className="text-xs text-muted-foreground">Moyenne</div></div>
                <div className="rounded-xl bg-cream p-4"><Trophy className="text-gold" size={20} /><div className="mt-1 font-display text-xl font-extrabold">{c.badges}</div><div className="text-xs text-muted-foreground">Badges</div></div>
              </div>
              {c.alerts?.length > 0 && (
                <div className="mt-4 space-y-2">
                  {c.alerts.map((a, i) => (
                    <div key={i} className="flex items-center gap-2 rounded-xl bg-destructive/5 px-4 py-2.5 text-sm text-destructive"><WarningCircle weight="fill" size={18} /> {a}</div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="rounded-2xl border border-dashed border-border bg-white p-10 text-center">
          <Users weight="duotone" className="mx-auto text-terracotta" size={32} />
          <p className="mt-2 text-sm text-muted-foreground">Aucun enfant lié. Saisissez le code de partage de votre enfant ci-dessus pour suivre sa progression.</p>
        </div>
      )}
    </div>
  );
}
